import { yupResolver } from '@hookform/resolvers/yup'
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import * as yup from 'yup'

import { ErrorMessage } from '@/app/components/ErrorMessage'
import { Input } from '@/app/components/Input'
import Modal, { ModalProps } from '@/app/components/Modal/Modal'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { SecondaryButton } from '@/app/components/SecondaryButton'
import { Select } from '@/app/components/Select'
import { useMyUser } from '@/app/hooks/data-hooks/useMyUser'
import { CloseFilled } from '@/app/icons/CloseFilled'
import { ICompany } from '@/types/company'
import { IEmailNamePair } from '@/types/email-name-pair.interface'
import { UserInclude, UserRole } from '@/types/user'
import { useNamespacedIntl } from '../lib/useNamespacedIntl'

export type InvitedUser = IEmailNamePair & {
  role: UserRole
}

type InvitationModalProps = Omit<ModalProps, 'title' | 'onSubmit'> & {
  invitedUsers?: InvitedUser[]
  onSubmit: SubmitHandler<InvitationFormValues>
}

export type InvitationFormValues = {
  invitedUsers: InvitedUser[]
}

export function InvitationModal({
  invitedUsers,
  onSubmit,
  ...props
}: InvitationModalProps) {
  const t = useNamespacedIntl('InvitationModal')
  const { data: { data: user } = {}, isLoading: isLoadingUser } = useMyUser({
    include: [UserInclude.Company],
  })

  const formSchema = yup.object({
    invitedUsers: yup
      .array()
      .of(
        yup.object({
          email: yup
            .string()
            .email()
            .required(t('email_required'))
            .test(
              'email-domain',
              t('email_must_be_from_domain'),
              (value, { path }) => {
                const emailDomain = value?.split('@')[1] || ''
                if (
                  !(user?.company as ICompany)?.domain ||
                  emailDomain.toLowerCase() !==
                    (user?.company as ICompany).domain?.toLowerCase()
                ) {
                  throw new yup.ValidationError(
                    t('email_must_be_from_domain'),
                    value,
                    path
                  )
                }
                return true
              }
            ),
          name: yup.string().optional(),
          role: yup
            .string()
            .oneOf(Object.values(UserRole))
            .required(t('role_required')),
        })
      )
      .min(1, t('at_least_one_collaborator_required')),
  })

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      invitedUsers:
        Array.isArray(invitedUsers) && invitedUsers.length
          ? invitedUsers
          : [
              {
                email: '',
                name: '',
                role: UserRole.Employer,
              },
            ],
    },
    resolver: yupResolver(formSchema),
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'invitedUsers',
  })

  return (
    <Modal
      {...props}
      className="md:w-[820px]"
      title={t('invite_collaborators')}
      subtitle={t('send_invite_emails')}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4 p-8 pt-0"
      >
        <div className="flex max-h-[300px] flex-col gap-1 overflow-y-auto py-1">
          {fields.map((field, index) => (
            <div key={field.id} className="flex w-full gap-2 text-sm">
              <Controller
                control={control}
                name={`invitedUsers.${index}.email`}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('email')}
                    className="flex-1"
                    placeholder=""
                    errorMessage={errors.invitedUsers?.[index]?.email?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name={`invitedUsers.${index}.name`}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('name')}
                    className="flex-1"
                    errorMessage={errors.invitedUsers?.[index]?.name?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name={`invitedUsers.${index}.role`}
                render={({ field }) => (
                  <Select
                    {...field}
                    id={`invitedUsers.${index}.role`}
                    label={t('role')}
                    placeholder={t('select_role')}
                    options={[UserRole.User, UserRole.Employer].map((role) => ({
                      label: t(`user_role.${role}`),
                      value: role,
                    }))}
                    errorMessage={errors.invitedUsers?.[index]?.role?.message}
                  />
                )}
              />
              <SecondaryButton
                className={`mt-9 aspect-square shrink-0 self-start`}
                onClick={() => remove(index)}
              >
                <CloseFilled className="h-4 w-4" />
              </SecondaryButton>
            </div>
          ))}
          <ErrorMessage
            message={errors.invitedUsers?.root?.message}
            className="mt-2 text-left"
          />
        </div>
        <SecondaryButton
          small
          className="self-start"
          onClick={() =>
            append({
              email: '',
              name: '',
              role: UserRole.Employer,
            })
          }
        >
          {t('add_collaborator')}
        </SecondaryButton>

        <div className="flex justify-end gap-4">
          <SecondaryButton type="button" onClick={props.onClose}>
            {t('cancel')}
          </SecondaryButton>
          <PrimaryButton disabled={isLoadingUser} type="submit">
            {t('send_invites')}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  )
}
